/**
 * Pure column helpers - prefix grouping, type classification,
 * range presence/coverage, and the dual-pane grouping. Ported verbatim from the
 * legacy blob's column-selector functions. No DOM, no atoms: these take plain
 * data so they're trivially unit-testable and reusable by the plot pickers
 * (§10.7, which also wrap column names and classify numeric types).
 */

import { html, type TemplateResult } from 'lit';
import { fuzzyMatch } from './fuzzy.ts';
import type { ColumnInfo } from '../api/types.ts';

export type PresenceStatus = 'full' | 'partial' | 'absent' | 'unknown';

export interface Presence {
  status: PresenceStatus;
  present: number;
  total: number;
}

/** True for DuckDB numeric column types. */
export function isNumericType(t: string): boolean {
  return /INT|FLOAT|DOUBLE|DECIMAL|BIGINT|SMALLINT|TINYINT/i.test(t);
}

/** True for DuckDB temporal types (timestamp/date/time) - scatter X allows these. */
export function isTemporalType(t: string): boolean {
  return /TIMESTAMP|DATE|TIME/i.test(t);
}

/**
 * Best-guess latitude/longitude column names for the Map plot, by name. Prefers
 * the canonical `Latitude`/`Longitude` (the fleet's NAPA names), then any
 * numeric column whose name looks like lat/lon. Returns `''` for either when no
 * candidate is found.
 */
export function detectLatLon(columns: ColumnInfo[]): { lat: string; lon: string } {
  const numeric = columns.filter((c) => isNumericType(c.type));
  // Canonical NAPA names win; otherwise fall back to a name pattern.
  const lat =
    numeric.find((c) => c.name === 'Latitude')?.name ??
    numeric.find((c) => /^lat(itude)?$/i.test(c.name))?.name ??
    numeric.find((c) => /latitude/i.test(c.name))?.name ??
    '';
  const lon =
    numeric.find((c) => c.name === 'Longitude')?.name ??
    numeric.find((c) => /^lon(g|gitude)?$/i.test(c.name))?.name ??
    numeric.find((c) => /longitude/i.test(c.name))?.name ??
    '';
  return { lat, lon };
}

/** Short, human label for a raw DuckDB type - num/int/text/bool/time/obj/list. */
export function friendlyType(rawType: string): string {
  const t = (rawType || '').toUpperCase();
  if (/DOUBLE|FLOAT|DECIMAL/.test(t)) return 'num';
  if (/BIGINT|SMALLINT|TINYINT|INT/.test(t)) return 'int';
  if (/VARCHAR|STRING|TEXT/.test(t)) return 'text';
  if (/BOOL/.test(t)) return 'bool';
  if (/TIMESTAMP|DATE|TIME/.test(t)) return 'time';
  if (/MAP|STRUCT/.test(t)) return 'obj';
  if (/ARRAY|LIST/.test(t)) return 'list';
  return (rawType || '').toLowerCase();
}

/**
 * Grouping prefix for a column name: the leading `[A-Za-z]+\d*` segment before
 * the first `_` (uppercased), else `Internal` for names starting with `_`, else
 * `Other`.
 */
export function getPrefix(name: string): string {
  const m = name.match(/^([A-Za-z]+\d*_)/);
  if (m) return m[1]!.replace(/_$/, '').toUpperCase();
  if (name.startsWith('_')) return 'Internal';
  return 'Other';
}

/** Preferred order for the built-in signal categories. Metadata-owned custom
 * categories follow these alphabetically, with Other always last. */
export const COLUMN_CATEGORY_ORDER = [
  'Derived signals',
  'Propulsion',
  'Energy',
  'Fuel & Consumption',
  'Navigation',
  'Environment',
  'Time & Identity',
] as const;

/**
 * Product category for the Export browser. Explicit metadata wins, allowing a
 * future catalog to own the taxonomy without another UI change. Older metadata
 * is classified conservatively from whole signal-name tokens; unmatched names
 * stay visible under Other.
 */
export function columnCategory(col: ColumnInfo): string {
  const explicit = col.category?.trim();
  if (explicit) return explicit;
  if (col.derived) return 'Derived signals';

  const name = col.name.toUpperCase();
  const token = (pattern: string): boolean =>
    new RegExp(`(^|_)(${pattern})(\\d+)?(_|$)`).test(name);

  if (isTemporalType(col.type) || token('DATE|DATETIME|TIMESTAMP|TIME|MONTH|SHIP|VESSEL')) {
    return 'Time & Identity';
  }
  if (token('FUEL|CONSUMPTION|CONSUMED|SFOC|LNG|HFO|MGO|LFO|VLSFO|BOG')) {
    return 'Fuel & Consumption';
  }
  if (token('LAT|LATITUDE|LON|LONG|LONGITUDE|SOG|STW|COG|HEADING|DRAFT|NAV|POSITION')) {
    return 'Navigation';
  }
  if (token('WIND|WEATHER|SEA|AIR|AMBIENT|WAVE')) return 'Environment';
  if (token('ME|ENGINE|SHAFT|PROPELLER|PROPULSION|THRUSTER|RPM')) return 'Propulsion';
  if (token('DG|GEN|GENERATOR|ELECTRIC|BATTERY|HOTEL|POWER|ENERGY')) return 'Energy';
  return 'Other';
}

export interface ColumnCategoryGroup {
  name: string;
  columns: ColumnInfo[];
}

/** Group columns into stable, human categories while preserving the backend's
 * alphabetical column order inside each category. */
export function groupColumnCategories(columns: ColumnInfo[]): ColumnCategoryGroup[] {
  const byCategory = new Map<string, ColumnInfo[]>();
  for (const col of columns) {
    const category = columnCategory(col);
    const list = byCategory.get(category);
    if (list) list.push(col);
    else byCategory.set(category, [col]);
  }
  const rank = new Map<string, number>(COLUMN_CATEGORY_ORDER.map((name, i) => [name, i]));
  return [...byCategory.entries()]
    .sort(([a], [b]) => {
      if (a === 'Other') return b === 'Other' ? 0 : 1;
      if (b === 'Other') return -1;
      const ar = rank.get(a);
      const br = rank.get(b);
      if (ar != null || br != null) return (ar ?? COLUMN_CATEGORY_ORDER.length) - (br ?? COLUMN_CATEGORY_ORDER.length);
      return a.localeCompare(b);
    })
    .map(([name, grouped]) => ({ name, columns: grouped }));
}

/**
 * Sorted list of `YYYY-MM` months overlapping the [start, end] range. Empty if
 * either bound is missing/invalid. Inclusive of both endpoint months, matching
 * the backend's month-level partition pruning.
 */
export function getSelectedRangeMonths(
  startStr: string | null | undefined,
  endStr: string | null | undefined,
): string[] {
  if (!startStr || !endStr) return [];
  const startMonth = startStr.slice(0, 7);
  const endMonth = endStr.slice(0, 7);
  if (startMonth.length !== 7 || endMonth.length !== 7) return [];
  const out: string[] = [];
  const sp = startMonth.split('-');
  const ep = endMonth.split('-');
  let y = Number(sp[0]);
  let m = Number(sp[1]);
  const endY = Number(ep[0]);
  const endM = Number(ep[1]);
  while (y < endY || (y === endY && m <= endM)) {
    out.push(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

/**
 * Whether a column has data across the selected range's months. `unknown` (no
 * months metadata, or no range selected) is treated as `full` for sorting.
 */
export function columnPresence(col: ColumnInfo, rangeMonths: string[]): Presence {
  if (!rangeMonths || rangeMonths.length === 0) return { status: 'unknown', present: 0, total: 0 };
  if (!Array.isArray(col.months) || col.months.length === 0) {
    return { status: 'unknown', present: 0, total: 0 };
  }
  const colSet = new Set(col.months);
  let present = 0;
  for (const month of rangeMonths) if (colSet.has(month)) present++;
  const total = rangeMonths.length;
  if (present === 0) return { status: 'absent', present, total };
  if (present === total) return { status: 'full', present, total };
  return { status: 'partial', present, total };
}

/** Sort tier so absent columns sink to the bottom of their group. */
export function presenceSortKey(status: PresenceStatus | undefined): number {
  if (status === 'full' || status === 'unknown' || status === undefined) return 0;
  if (status === 'partial') return 1;
  return 2; // absent
}

export interface GroupedColumns {
  /** Prefixes with ≥3 columns - rendered as collapsible groups. */
  realGroups: string[];
  /** Columns whose prefix has <3 members - folded into the "Other" group. */
  ungrouped: ColumnInfo[];
  /** prefix → its columns (presence-sorted). */
  groups: Record<string, ColumnInfo[]>;
}

/**
 * Group columns by prefix, applying a fuzzy name search filter (see
 * `utils/fuzzy.ts` - tolerates typos and spaces, so "proplsion power" finds
 * `propulsion_power`) and sorting each group full/unknown → partial → absent
 * (stable within tier, so the backend's alphabetical order is preserved).
 * Prefixes with <3 columns are collected into `ungrouped`.
 */
export function groupColumns(
  columns: ColumnInfo[],
  search: string,
  presence: Map<string, Presence>,
): GroupedColumns {
  const groups: Record<string, ColumnInfo[]> = {};
  const term = search.trim();
  for (const col of columns) {
    if (term && !fuzzyMatch(term, col.name)) continue;
    const prefix = getPrefix(col.name);
    let arr = groups[prefix];
    if (!arr) groups[prefix] = arr = [];
    arr.push(col);
  }

  const byPresence = (a: ColumnInfo, b: ColumnInfo): number =>
    presenceSortKey(presence.get(a.name)?.status) - presenceSortKey(presence.get(b.name)?.status);
  for (const arr of Object.values(groups)) arr.sort(byPresence);

  const sortedPrefixes = Object.keys(groups).sort();
  const realGroups: string[] = [];
  const ungrouped: ColumnInfo[] = [];
  for (const prefix of sortedPrefixes) {
    const arr = groups[prefix]!;
    if (arr.length < 3) ungrouped.push(...arr);
    else realGroups.push(prefix);
  }
  ungrouped.sort(byPresence);
  return { realGroups, ungrouped, groups };
}

/**
 * All columns belonging to a prefix (`_ungrouped` → every column whose prefix
 * has <3 members). Operates on the full column set, independent of any search
 * filter, so group select/deselect always covers the whole group.
 */
export function getGroupCols(allColumns: ColumnInfo[], prefix: string): ColumnInfo[] {
  if (prefix === '_ungrouped') {
    const counts: Record<string, number> = {};
    for (const c of allColumns) {
      const p = getPrefix(c.name);
      counts[p] = (counts[p] || 0) + 1;
    }
    return allColumns.filter((c) => (counts[getPrefix(c.name)] ?? 0) < 3);
  }
  return allColumns.filter((c) => getPrefix(c.name) === prefix);
}

/** Tri-state for a group's select-all checkbox. */
export function getGroupCheckState(
  cols: ColumnInfo[],
  selected: Set<string>,
): 'none' | 'all' | 'partial' {
  if (cols.length === 0) return 'none';
  const n = cols.filter((c) => selected.has(c.name)).length;
  if (n === 0) return 'none';
  if (n === cols.length) return 'all';
  return 'partial';
}

/**
 * Average null-% across the columns an export would include (`updateMissingExportStat`). The picked set is the explicit selection, or - when
 * nothing is selected - every column except the implicit `ship_id`/`month`
 * partition keys. Returns the average percentage and a per-column breakdown
 * (only columns with nulls), or null when there's nothing to measure.
 */
export function exportMissingStat(
  allColumns: ColumnInfo[],
  selected: Set<string>,
): { avg: number; perCol: string[] } | null {
  const picked = selected.size
    ? allColumns.filter((c) => selected.has(c.name))
    : allColumns.filter((c) => c.name !== 'ship_id' && c.name !== 'month');
  if (picked.length === 0) return null;

  let sumPct = 0;
  const perCol: string[] = [];
  for (const c of picked) {
    const p = Number(c.null_pct) || 0;
    sumPct += p;
    if (p > 0) perCol.push(`${c.name}: ${p}%`);
  }
  return { avg: sumPct / picked.length, perCol };
}

/**
 * Render a column name with a `<wbr>` after each `_`/`-` so long names wrap at
 * separators (CSS `overflow-wrap: anywhere` handles separator-less names). Lit
 * escapes the text segments; the `<wbr>` markers are real break opportunities.
 */
export function wrapColName(name: string): TemplateResult {
  const segs: string[] = [];
  let cur = '';
  for (const ch of name) {
    cur += ch;
    if (ch === '_' || ch === '-') {
      segs.push(cur);
      cur = '';
    }
  }
  if (cur) segs.push(cur);
  return html`${segs.map((s, i) =>
    i < segs.length - 1 ? html`${s}<wbr />` : html`${s}`,
  )}`;
}
